<template>
    <!--
        Conditional rendering based on manufacturer_name:

        1. If manufacturer_name exists (hasManufacturerName):
           - Wraps content in an expandable row (retr-current-single-table-row)
           - Shows manufacturer name as label with warning icon if conditions are unrealized
           - Displays retro value
           - Content is collapsible/expandable

        2. If manufacturer_name doesn't exist:
           - Renders content directly without expandable wrapper
           - Shows the same data but in a flat, non-expandable format

        Note: RetrCurrentManufacturerContent component is reused in both cases
        to avoid code duplication. This ensures maintainability and consistency.
    -->
    <div class="retr__rows-group">
        <retr-current-single-table-row
            v-if="hasManufacturerName"
            ref="manufacturerRow"
        >
            <template #label>
                <div class="retr-current__manufacturer">
                    {{ manufacturer.manufacturer_name }}
                    <a-icon
                        v-if="hasUnrealizedConditions"
                        v-tooltip="'Istnieje co najmniej jeden niespełniony warunek'"
                        icon="Warning"
                        class="text-danger"
                        width="16px"
                        height="16px"
                    />
                </div>
            </template>
            <template #value>
                {{ manufacturer.retro | currency }}
            </template>
            <template #content>
                <!-- Manufacturer sales net amount -->
                <div
                    v-if="manufacturerSalesNet !== null"
                    class="retr-current__condition-group"
                >
                    <retr-current-condition-item
                        :condition="
                            { condition_name: 'Wartość obrotu', retro: manufacturerSalesNet }
                        "
                        :show-value-percentage="false"
                    />
                </div>

                <!-- Condition groups -->
                <retr-current-condition-group
                    v-for="(conditionGroup, conditionGroupIndex) in sortedConditionGroups"
                    :key="`manufacturer-${manufacturer.manufacturer_id}-conditionGroup-${conditionGroupIndex}`"
                    :condition-group="conditionGroup"
                    :manufacturer-name="manufacturer.manufacturer_name ?? ''"
                />

                <div
                    v-if="manufacturer.manufacturer_name"
                    class="retr-manufacturer-total-amount typo--name"
                >
                    <span>TOTAL premia (MAX przy obecnym obrocie)</span>
                    <span>{{ manufacturer.retro | currency }}</span>
                </div>
            </template>
        </retr-current-single-table-row>

        <!-- Same content rendered directly when no manufacturer name -->
        <retr-current-manufacturer-content
            v-else
            :manufacturer="manufacturer"
            :manufacturer-sales-net="manufacturerSalesNet"
        />
    </div>
</template>

<script>
import RetrCurrentSingleTableRow from '@/components/my-business/retr/RetrCurrentSingleTableRow.vue'
import RetrCurrentManufacturerContent from '@/components/my-business/retr/RetrCurrentManufacturerContent.vue'
import RetrCurrentConditionGroup from '@/components/my-business/retr/RetrCurrentConditionGroup.vue'
import RetrCurrentConditionItem from '@/components/my-business/retr/RetrCurrentConditionItem.vue'
import { sortConditionGroupsByOrder } from '@/helpers/retr'

/**
 * RetrCurrentManufacturerRow Component
 *
 * Displays manufacturer data (name, retro value, conditions) with conditional expandable behavior:
 * - With manufacturer_name: Shows as an expandable row with header (name + retro value)
 * - Without manufacturer_name: Shows content directly without expandable wrapper
 *
 * This dual-mode approach allows flexible UI presentation while avoiding code duplication
 * by extracting shared content into RetrCurrentManufacturerContent component.
 */
export default {
    name: 'RetrCurrentManufacturerRow',
    components: {
        RetrCurrentSingleTableRow,
        RetrCurrentManufacturerContent,
        RetrCurrentConditionGroup,
        RetrCurrentConditionItem
    },
    props: {
        manufacturer: {
            type: Object,
            required: true,
            validator: (value) => {
                return value &&
                    typeof value.manufacturer_id !== 'undefined' &&
                    Array.isArray(value.list)
            }
        }
    },
    computed: {
        /**
         * Check if manufacturer has a name
         *
         * This determines the rendering mode:
         * - true: Render as expandable row with header
         * - false: Render content directly without wrapper
         *
         * @returns {Boolean} True if manufacturer_name exists and is truthy
         */
        hasManufacturerName() {
            return Boolean(this.manufacturer.manufacturer_name)
        },
        /**
         * Check if manufacturer has at least one unrealized condition
         * Traverses through all condition groups, types and conditions
         * @returns {Boolean}
         */
        hasUnrealizedConditions() {
            if (!this.manufacturer.list || !Array.isArray(this.manufacturer.list)) {
                return false
            }

            return this.manufacturer.list.some(conditionGroup => {
                if (!conditionGroup.list || !Array.isArray(conditionGroup.list)) {
                    return false
                }

                // Skip condition groups with sales goals progress (they use threshold-based evaluation)
                if (conditionGroup.sales_result !== null &&
                    conditionGroup.sales_goals &&
                    Array.isArray(conditionGroup.sales_goals) &&
                    conditionGroup.sales_goals.length > 0) {
                    return false
                }

                return conditionGroup.list.some(conditionType => {
                    if (!conditionType.list || !Array.isArray(conditionType.list)) {
                        return false
                    }

                    // If conditionType has minimum_sku, check if minimum conditions are realized
                    if (conditionType.minimum_sku !== null && conditionType.minimum_sku !== undefined) {
                        const realizedCount = conditionType.indexes && Array.isArray(conditionType.indexes)
                            ? conditionType.indexes.filter(index => index.is_condition_realized).length
                            : 0

                        return realizedCount < conditionType.minimum_sku
                    }

                    // Otherwise use default logic - check if all conditions are realized
                    return conditionType.list.some(condition => {
                        return !condition.is_condition_realized
                    })
                })
            })
        },
        /**
         * Extract manufacturer's sales net value
         *
         * Traverses the nested data structure (manufacturer → conditionGroups → conditionTypes → conditions)
         * to find the first non-null sales_net value. This value represents the manufacturer's total sales
         * and is displayed as "Wartość obrotu" (Sales Amount).
         *
         * @returns {Number|null} First found sales_net value or null if not found
         */
        manufacturerSalesNet() {
            if (!this.manufacturer.list || !Array.isArray(this.manufacturer.list)) {
                return null
            }

            for (const conditionGroup of this.manufacturer.list) {
                if (!conditionGroup.list || !Array.isArray(conditionGroup.list)) {
                    continue
                }

                for (const conditionType of conditionGroup.list) {
                    if (!conditionType.list || !Array.isArray(conditionType.list)) {
                        continue
                    }

                    for (const condition of conditionType.list) {
                        if (condition.sales_net !== null && condition.sales_net !== undefined) {
                            return condition.sales_net
                        }
                    }
                }
            }

            return null
        },
        sortedConditionGroups() {
            return sortConditionGroupsByOrder(this.manufacturer.list)
        }
    },
    methods: {
        /**
         * Expand this manufacturer row
         *
         * PUBLIC method called from parent component.
         * Only works when manufacturer has a name (expandable mode).
         * Does nothing if row is already expanded or if in non-expandable mode.
         */
        expandRow() {
            if (this.hasManufacturerName && this.$refs.manufacturerRow && !this.$refs.manufacturerRow.isExpanded) {
                this.$refs.manufacturerRow.expand()
            }
        }
    }
}
</script>

<style lang="scss">
@import '../../../styles/components/retr-common';

.retr-current {
    &__manufacturer {
        display: flex;
        align-items: center;
        gap: 1rem;
    }
}
.retr-manufacturer-total-amount {
    padding: 12px calc(var(--button-space) + 1rem) 12px 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    background-color: var(--active-promo-bg);
    font-weight: 600;
}
</style>
